import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import type { ComponentType, FC } from 'react';

import AuFlag from 'assets/flags/AuFlag';
import CaFlag from 'assets/flags/CaFlag';
import NzFlag from 'assets/flags/NzFlag';
import UkFlag from 'assets/flags/UkFlag';

import useTranslate from 'hooks/useTranslate';
import type { BillingRevenueCountryCode } from 'types';

interface CountryFlagProps {
    height?: string;
    width?: string;
}

interface RevenueCountryOption {
    code: BillingRevenueCountryCode;
    Flag: ComponentType<CountryFlagProps>;
    labelKey: string;
}

const COUNTRY_OPTIONS: RevenueCountryOption[] = [
    { code: 'AU', Flag: AuFlag, labelKey: 'australia' },
    { code: 'CA', Flag: CaFlag, labelKey: 'canada' },
    { code: 'UK', Flag: UkFlag, labelKey: 'unitedKingdom' },
    { code: 'NZ', Flag: NzFlag, labelKey: 'newZealand' },
];

export const REVENUE_COUNTRY_CODES = COUNTRY_OPTIONS.map(({ code }) => code);

interface RevenueCountryFlagsProps {
    value: BillingRevenueCountryCode[];
    onChange: (countries: BillingRevenueCountryCode[]) => void;
}

const RevenueCountryFlags: FC<RevenueCountryFlagsProps> = ({ value, onChange }) => {
    const { translate } = useTranslate();

    return (
        <ToggleButtonGroup
            size="small"
            value={value}
            onChange={(_event, nextCountries: BillingRevenueCountryCode[]) => {
                if (nextCountries.length) {
                    onChange(nextCountries);
                }
            }}
            aria-label={translate('serviceProviderBilling.revenue.countryFilter')}
        >
            {COUNTRY_OPTIONS.map(({ code, Flag, labelKey }) => {
                const label = translate(labelKey);
                const isOnlySelectedCountry = value.length === 1 && value[0] === code;
                return (
                    <Tooltip key={code} title={label}>
                        <ToggleButton
                            value={code}
                            aria-label={label}
                            disabled={isOnlySelectedCountry}
                            sx={{ px: 1, py: 0.75, lineHeight: 0 }}
                        >
                            <Flag width="28" height="21" />
                        </ToggleButton>
                    </Tooltip>
                );
            })}
        </ToggleButtonGroup>
    );
};

export default RevenueCountryFlags;
